'use client';
import React, { useState, useEffect } from 'react';

const navItems = [
  { label: 'Home', href: '#home' },
  {
    label: 'Tours',
    href: '#services',
    children: [
      { label: 'Private Morocco Tours', href: '#services' },
      { label: 'Jewish Heritage Tours', href: '#jewish-heritage' },
      { label: 'Casablanca Tours', href: '#services' },
      { label: 'Day Trips', href: '#services' },
      { label: 'Luxury Chauffeur', href: '#services' },
      { label: 'Airport Transfers', href: '#services' },
      { label: 'Cruise Shore Excursions', href: '#services' },
      { label: 'Custom Tours', href: '#services' },
    ],
  },
  {
    label: 'Destinations',
    href: '#destinations',
    children: [
      { label: 'Casablanca', href: '#destinations' },
      { label: 'Marrakech', href: '#destinations' },
      { label: 'Fes', href: '#destinations' },
      { label: 'Rabat', href: '#destinations' },
      { label: 'Essaouira', href: '#destinations' },
      { label: 'Tangier', href: '#destinations' },
      { label: 'Chefchaouen', href: '#destinations' },
      { label: 'Sahara Desert', href: '#destinations' },
    ],
  },
  { label: 'Jewish Heritage', href: '#jewish-heritage' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Blog', href: '#blog' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'hidden' : 'block'
      }`}>
        <div className="bg-navy-dark/95 py-1.5 border-b border-gold/20">
          <div className="container-luxury flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a href="tel:+212668702424" className="flex items-center gap-1.5 font-sans text-xs text-white/70 hover:text-gold transition-colors">
                <svg className="w-3 h-3 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +212 668 702 424
              </a>
              <a href="mailto:dbdahantours@gmail.com" className="hidden sm:flex items-center gap-1.5 font-sans text-xs text-white/70 hover:text-gold transition-colors">
                <svg className="w-3 h-3 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                dbdahantours@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-sans text-xs text-white/60">🇺🇸 🇫🇷 🇲🇦 🇮🇱</span>
              <a
                href="https://wa.me/212668702424?text=Hello%20David%2C%20I%20am%20interested%20in%20a%20private%20tour%20in%20Morocco."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 font-sans text-xs font-bold text-green-400 hover:text-green-300 transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'top-0 bg-white shadow-luxury py-3'
            : 'top-8 bg-transparent py-4'
        }`}
      >
        <div className="container-luxury">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => handleNavClick('#home')}
              className="flex flex-col items-start group"
            >
              <span
                className={`font-serif text-xl font-bold tracking-wide transition-colors duration-300 ${
                  scrolled ? 'text-navy' : 'text-white'
                }`}
              >
                Morocco
                <span className="text-gold">Jewish</span>Tours
              </span>
              <span className="font-sans text-xs tracking-widest uppercase text-gold/90">
                Private Luxury Experiences
              </span>
            </button>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-5">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    onClick={() => handleNavClick(item.href)}
                    className={`nav-link pb-1 flex items-center gap-1 ${
                      scrolled ? 'text-navy' : 'text-white hover:text-gold'
                    }`}
                  >
                    {item.label}
                    {item.children && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {/* Dropdown */}
                  {item.children && activeDropdown === item.label && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-white shadow-luxury border-t-2 border-gold z-50">
                      <div className="py-2">
                        {item.children.map((child) => (
                          <button
                            key={child.label}
                            onClick={() => handleNavClick(child.href)}
                            className="block w-full text-left px-5 py-2.5 font-sans text-sm text-navy hover:text-gold hover:bg-cream transition-colors duration-200"
                          >
                            {child.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="https://wa.me/212668702424?text=Hello%20David%2C%20I%20am%20interested%20in%20a%20private%20tour%20in%20Morocco."
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 transition-colors duration-200 ${
                  scrolled ? 'text-green-600 hover:text-green-700' : 'text-green-400 hover:text-green-300'
                }`}
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-gold text-xs px-5 py-2.5"
              >
                Book Now
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 transition-colors duration-200 ${
                scrolled ? 'text-navy' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gold/20 shadow-luxury">
            <div className="container-luxury py-4 space-y-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleNavClick(item.href)}
                      className="flex-1 text-left px-4 py-3 font-sans text-sm font-semibold text-navy hover:text-gold hover:bg-cream transition-colors duration-200 uppercase tracking-wider"
                    >
                      {item.label}
                    </button>
                    {item.children && (
                      <button
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                        className="px-3 py-3 text-gold"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            mobileExpanded === item.label ? 'rotate-180' : ''
                          }`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {item.children && mobileExpanded === item.label && (
                    <div className="pl-6 space-y-1 pb-2">
                      {item.children.map((child) => (
                        <button
                          key={child.label}
                          onClick={() => handleNavClick(child.href)}
                          className="block w-full text-left px-4 py-2 font-sans text-xs text-gray-600 hover:text-gold transition-colors duration-200"
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-3 border-t border-gold/20">
                <button
                  onClick={() => handleNavClick('#contact')}
                  className="btn-gold w-full text-center"
                >
                  Book Your Tour
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
